import { AutomationInterface, AutomationsResponseInterface } from '@app/models/automations';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { resetForm } from '@app/redux/features/campaignForm';
import { getAutomations } from '@app/services/automations.ts';

export const useAutomations = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  // const [createSecheduleModal, setCreateScheduleModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState<AutomationInterface | undefined>();
  const [cancelModal, setCancelModal] = useState<AutomationInterface | undefined>();

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [sortBy, setSortBy] = useState<string | undefined>();
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [currentFilterStatus, setCurrentFilterStatus] = useState<string>('all');

  const filterForm = useForm();
  const [searchValue, setSearchValue] = useState('');

  const {
    data: automationsData,
    isLoading,
    refetch,
  } = useQuery<AutomationsResponseInterface>({
    queryKey: ['automations', page, limit, sortBy, sortOrder, currentFilterStatus, filterForm.watch(), filterForm.watch('statuses'), filterForm.watch('scheduled'), filterForm.watch('platforms')],
    queryFn: () => {
      const formValues = filterForm.getValues();

      // Process filter values to match API expectations
      let statuses = undefined;

      if (formValues.statuses) {
        if (Array.isArray(formValues.statuses) && formValues.statuses.length > 0) {
          // Handle multi-select status filter
          statuses = formValues.statuses.map((status: string) => status.toUpperCase());
        } else if (!Array.isArray(formValues.statuses) && formValues.statuses !== 'all') {
          // Handle single status filter
          statuses = [formValues.statuses.toUpperCase()];
        }
      }

      const channels = formValues.channels && formValues.channels !== 'all' ? (Array.isArray(formValues.channels) ? formValues.channels : [formValues.channels]) : undefined;
      // Always return all campaigns regardless of platform selection

      return getAutomations({
        page,
        limit,
        sortBy: sortBy || 'createdAt',
        sortOrder,
        statuses,
        channels,
        tab: currentFilterStatus,
        from: formValues.from,
        to: formValues.to,
        search: searchValue,
      })
        .then((res) => {
          const responseData = res?.data?.data || res?.data;
          return responseData;
        })
        .catch((err) => {
          console.error('Error fetching campaigns:', err);
          // Return a valid data structure instead of the error
          return { campaigns: [], total: 0 };
        });
    },
  });

  const handleDeleteModal = (automation?: AutomationInterface) => {
    setDeleteModal(automation);
  };

  const handleCancelModal = (automation?: AutomationInterface) => {
    setCancelModal(automation);
  };


  //
  // const handleCreateScheduleModal = () => {
  //   setCreateScheduleModal(!createSecheduleModal);
  // };

  const createAutomation = () => {
    dispatch(resetForm());
    localStorage.removeItem('automationFormData');
    localStorage.removeItem('automationSelectedLists');
    navigate('/automations/create/settings');
  };

  const handleLimitChange = (newLimit: number | undefined) => {
    setLimit(newLimit ?? 10);
    setPage(1);
  };

  const handleNextPage = () => {
    setPage((prev) => prev + 1);
  };

  const handlePreviousPage = () => {
    setPage((prev) => Math.max(prev - 1, 1));
  };

  const handleSortingTable = (field: string) => {
    // For platforms column, sort by name instead
    const actualSortField = field === 'platforms' ? 'name' : field;

    if (sortBy === actualSortField) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(actualSortField);
      setSortOrder('asc');
    }
  };

  useEffect(() => {
    // Refetch when form values change (excluding search field)
    const subscription = filterForm.watch((_, { name }) => {
      // Skip search field changes - they are handled separately
      if (name === 'search') {
        return;
      }
      refetch();
    });
    return () => subscription.unsubscribe();
  }, [filterForm, refetch]);

  // Handle search value changes separately
  useEffect(() => {
    // Only refetch if search value has 3+ characters or is empty
    if (searchValue.length >= 3 || searchValue === '') {
      refetch();
    }
  }, [searchValue, refetch]);
  return {
    // handleCreateScheduleModal,
    // createSecheduleModal,
    automationsData,
    isLoading,
    refetch,
    createAutomation,
    createCampaign: createAutomation,
    handleDeleteModal,
    deleteModal,
    handleCancelModal,
    cancelModal,
    // Return pagination values and handlers
    page,
    limit,
    sortBy,
    sortOrder,
    currentFilterStatus,
    setCurrentFilterStatus,
    handleLimitChange,
    handleNextPage,
    handlePreviousPage,
    handleSortingTable,
    filterForm,
    searchValue,
    setSearchValue,
  };
};
